import { UserMatchFilter } from 'data/user/types'
import { chain, clone } from 'lodash'
import { MatchFilter, PredefinedMatchingFilter } from 'scenes/Dashboard/scenes/OrganizationsManager/duck/types'
import { ApiCaller, callApi, getUrlWithPathParams } from 'services/api/utils'
import { Writeable } from 'utils-next/types'
import { normalizeDataForApi } from 'utils/api'

import { UserStatus } from '../../../../entities-next/user'
import { Extraction, Organization, OrganizationLocator, OrganizationMatchStatus } from './types'

const ORGANIZATIONS_ENDPOINT_PATH = '/organizations/'
export const ORGANIZATIONS_MANAGER_ENDPOINT_PATH = '/organizations/manager'
const EXTRACTIONS_ENDPOINT_PATH = '/extractions/'

const apiGetOrganizationFactory = (apiCaller: ApiCaller) => async (id: number): Promise<ApiOrganization> => {
  const organizationData = await apiCaller<ApiOrganization>({
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, id),
  })

  return normalizeApiOrganization(organizationData)
}

export const apiGetOrganization = apiGetOrganizationFactory(callApi)
export const apiGetOrganizationAsAdmin = apiGetOrganizationFactory(callApi)

export function formatOrganization(organizations: ApiOrganization[]): ApiIdIndexedOrganizations {
  return chain(organizations).map(normalizeApiOrganization).keyBy('id').value()
}

export async function apiGetSetOfOrganizationsFromManager(
  organizationIds: Organization['id'][]
): Promise<ApiIdIndexedOrganizations> {
  const organizationsData = await callApi<ApiOrganization[]>({
    url: ORGANIZATIONS_MANAGER_ENDPOINT_PATH,
    params: {
      ids: organizationIds,
    },
  })

  const organizationsWithFundingEvents = organizationsData.map((organization) => {
    return organization
  })
  return formatOrganization(organizationsWithFundingEvents)
}

export const apiGetCurrentUserOrganizations = async (
  currentPage: number = 0,
  limit?: number,
  search?: string
): Promise<ApiIdIndexedOrganizations> => {
  const organizationsData = await callApi<ApiOrganization[]>({
    url: ORGANIZATIONS_ENDPOINT_PATH,
    params: {
      skip: limit ? currentPage * limit : 0,
      limit,
      search,
    },
  })
  return formatOrganization(organizationsData)
}

export const apiGetAllOrganizations = async (
  currentPage: number = 0,
  limit?: number,
  search?: string
): Promise<ApiIdIndexedOrganizations> => {
  const organizationsData = await callApi<ApiOrganization[]>({
    url: ORGANIZATIONS_MANAGER_ENDPOINT_PATH,
    params: {
      skip: limit ? currentPage * limit : 0,
      limit,
      search,
    },
  })
  const organizationsWithFundingEvents = organizationsData.map((organization) => {
    return organization
  })

  return formatOrganization(organizationsWithFundingEvents)
}

export const apiPutOrganizationAsAdmin = async (organization: Partial<ApiOrganization>): Promise<ApiOrganization> =>
  callApi<ApiOrganization>({
    method: 'put',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organization.id),
    data: normalizeDataForApi(organization),
  })

export const apiPostOrganizationAsAdmin = async (
  organizationLocator: OrganizationLocator,
  isCreated: boolean
): Promise<ApiOrganization> =>
  callApi<ApiOrganization>({
    method: 'post',
    url: ORGANIZATIONS_MANAGER_ENDPOINT_PATH,
    data: {
      organization: normalizeDataForApi(organizationLocator),
      isCreated,
    },
  })

export async function apiReleaseOrganizationMatches(organizationId: Organization['id']) {
  return await callApi<ApiOrganization>({
    method: 'put',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organizationId, 'release-matches'),
  })
}

export async function apiDeleteOrganization(organizationId: Organization['id']): Promise<void> {
  return await callApi<void>({
    method: 'DELETE',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organizationId),
  })
}

export async function apiCreateOrganizationMatch(
  organizationId: Organization['id'],
  matchFilter: MatchFilter | undefined
): Promise<void> {
  let userFilter: UserMatchFilter | undefined
  let predefinedMatchingFilter: PredefinedMatchingFilter | undefined

  switch (matchFilter) {
    case MatchFilter.allZombies:
      userFilter = { status: UserStatus.zombie }
      break
    case MatchFilter.filteredZombies:
      predefinedMatchingFilter = PredefinedMatchingFilter.filteredZombies
      break
    default:
      userFilter = { status: UserStatus.active }
  }

  return await callApi<void>({
    method: 'POST',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organizationId, 'match'),
    data: { userFilter, predefinedMatchingFilter },
  })
}

export async function apiExtractData(organizationId: Organization['id']): Promise<Extraction> {
  return await callApi<Extraction>({
    method: 'POST',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organizationId, 're-extract'),
  })
}

export async function apiGetExtraction(extractionId: Extraction['id']): Promise<Extraction> {
  return await callApi<Extraction>({
    method: 'GET',
    url: getUrlWithPathParams(EXTRACTIONS_ENDPOINT_PATH, extractionId),
  })
}

const apiGetOrganizationMatchStatus = (apiCaller: ApiCaller) => async (
  organization: Organization
): Promise<OrganizationMatchStatus> => {
  const res = await apiCaller<OrganizationMatchStatus>({
    method: 'get',
    url: getUrlWithPathParams(ORGANIZATIONS_MANAGER_ENDPOINT_PATH, organization.id, 'match-status'),
  })
  return res
}

export const apiGetOrganizationMatchStatusAsAdmin = apiGetOrganizationMatchStatus(callApi)

function normalizeApiOrganization(organization: ApiOrganization): ApiOrganization {
  const normalizedOrganization = clone(organization)
  normalizedOrganization.foundedOn = normalizedOrganization.foundedOn
    ? new Date(normalizedOrganization.foundedOn)
    : undefined
  normalizedOrganization.reportedLikelihoodFundraising = normalizedOrganization.reportedLikelihoodFundraising
    ? new Date(normalizedOrganization.reportedLikelihoodFundraising)
    : undefined
  normalizedOrganization.currentRoundAnnouncedOn = normalizedOrganization.currentRoundAnnouncedOn
    ? new Date(normalizedOrganization.currentRoundAnnouncedOn)
    : undefined
  normalizedOrganization.lastRoundClosedOn = normalizedOrganization.lastRoundClosedOn
    ? new Date(normalizedOrganization.lastRoundClosedOn)
    : undefined
  return normalizedOrganization
}

type ApiIdIndexedOrganizations = {
  [organizationId: number]: ApiOrganization
}

export type ApiOrganization = Writeable<Organization>
